import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link, useNavigate } from "react-router-dom";
import api from "@/utils/api";
import {
  Factory,
  Building2,
  Users,
  FileText,
  Lock,
  Eye,
  HelpCircle,
} from "lucide-react";
import OldConventionalForm from "./forms/OldConventionalForm";
import NewLoomForm from "./forms/NewLoomForm";
import CommonFacilityCentreForm from "./forms/CommonFacilityCentreForm";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import StatusBadge from "../components/common/StatusBadge";
import HowToApplyUpgradation from "../assets/pdf/how_to_apply_upgradation.pdf";
import HowToApplyNewShuttleLess from "../assets/pdf/how_to_apply_newshuttleless.pdf";
import HowToApplyCFC from "../assets/pdf/how_to_apply_cfc.pdf";

const Forms = () => {
  const navigate = useNavigate();
  const [formsStatus, setFormsStatus] = useState({
    allow_to_apply: false,
    completed_forms: null,
  });
  const [applicationsData, setApplicationData] = useState({
    modernization: [],
    new_shuttleless: [],
    cfc_subsidies: [],
    air_jet_loom: [],
  });
  const [applicationDetails, setApplicationDetails] = useState({
    power_loom: null,
    shuttle_less: null,
    cfc: null,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkFormsStatus();
  }, []);

  const checkFormsStatus = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/profile/check-completed`);
      setFormsStatus(res.data);
      setApplicationDetails(res.data?.application_details);
      setApplicationData(res.data?.applications);
    } catch (error) {
      console.error("Error checking forms status:", error);
    } finally {
      setLoading(false);
    }
  };

  const formsData = [
    {
      id: "powerloom-upgradation",
      key: "modernization",
      title:
        "Upgradation of 3 year Old Conventional Powerloom into Rapier Shuttleless Loom",
      description:
        "Application for Upgradation of 3 year old conventional Powerloom to Rapier Shuttleless Loom",
      icon: Factory,
      total: applicationDetails?.power_loom?.total ?? "0",
      remaining: applicationDetails?.power_loom?.available ?? "0",
      applied: applicationDetails?.power_loom?.applied ?? "0",
      user_remaining: applicationDetails?.power_loom?.user_remaining ?? "0",
      component: OldConventionalForm,
      how_to_apply: HowToApplyUpgradation,
    },
    {
      id: "new-loom-purchase",
      key: "new_shuttleless",
      title:
        "Purchase of New Rapier Loom / Replacement of existing Low Speed Rapier Looms",
      description:
        "Application for Purchase of New Rapier Loom / Replacement of existing Low Speed Rapier Looms",
      icon: Building2,
      total: applicationDetails?.shuttle_less?.total ?? "0",
      remaining: applicationDetails?.shuttle_less?.available ?? "0",
      applied: applicationDetails?.shuttle_less?.applied ?? "0",
      user_remaining: applicationDetails?.shuttle_less?.user_remaining ?? "0",
      component: NewLoomForm,
      how_to_apply: HowToApplyNewShuttleLess,
    },
    {
      id: "common-facility-centre",
      key: "cfc",
      title: "Common Facility Centre ",
      description:
        "Application for Common Facility Centre  under Powerloom Modernization Scheme",
      icon: Users,
      total: applicationDetails?.cfc?.total ?? "0",
      remaining: applicationDetails?.cfc?.available ?? "0",
      applied: applicationDetails?.cfc?.applied ?? "0",
      user_remaining: applicationDetails?.cfc?.user_remaining ?? "0",
      component: CommonFacilityCentreForm,
      how_to_apply: HowToApplyCFC,
    },
  ];

  const getFormStatus = (formKey) => {
    const { allow_to_apply, completed_forms } = formsStatus;
    const isCompleted = completed_forms && completed_forms[formKey];

    if (isCompleted) {
      return "completed";
    }

    if (!allow_to_apply) {
      return "unavailable";
    }

    if (completed_forms && Object.keys(completed_forms).length > 0) {
      const completedFormKeys = Object.keys(completed_forms);
      if (
        completedFormKeys.length > 0 &&
        !completedFormKeys.includes(formKey)
      ) {
        return "unavailable";
      }
    }

    return "available";
  };

  const handleDownload = (fileUrl, fileName) => { 
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = fileName;
    link.target = "_blank";  
    link.rel = "noopener noreferrer"; 
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFormClick = async (form) => {
    navigate(`/scheme/${form?.key}`);
  };

  const allApplications = [
    ...applicationsData.modernization.map((app) => ({
      ...app,
      type: "modernization",
      title: "Upgradation",
    })),
    ...applicationsData.new_shuttleless.map((app) => ({
      ...app,
      type: "new_shuttleless",
      title: "New Shuttleless",
    })),
    ...applicationsData.cfc_subsidies.map((app) => ({
      ...app,
      type: "cfc",
      title: "CFC Scheme",
    })),
    ...applicationsData?.air_jet_loom.map((app) => ({
      ...app,
      type: "new_airjetlooms",
      title: "Upgradation",
    })),
  ];

  const { allow_to_apply, completed_forms } = formsStatus;
  const hasCompletedForms =
    completed_forms && Object.keys(completed_forms).length > 0;

  if (loading) {
    return (
      <div className="bg-background flex items-center justify-center min-h-[88vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Checking form availability...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background p-4 rounded space-y-6 max-w-[95vw] mx-auto min-h-[88vh]">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">
            Powerloom Modernisation Scheme 2025-26
          </h1>
          <p className="text-muted-foreground mt-2">
            Choose the appropriate form for your application
          </p>
        </div>
        <Badge variant="secondary" className="text-sm">
          {
            formsData.filter(
              (form) => getFormStatus(form.key) !== "unavailable"
            ).length
          }{" "}
          - Scheme Available
        </Badge>
      </div>

      {!allow_to_apply && !hasCompletedForms && (
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-yellow-800">
              <Lock className="h-4 w-4" />
              <p className="text-sm font-medium">
                Form submission is currently unavailable. Please complete
                prerequisite requirements.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {!allow_to_apply && hasCompletedForms && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-blue-800">
              <Eye className="h-4 w-4" />
              <p className="text-sm font-medium">
                You can view your completed applications below. New submissions
                are currently unavailable.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {formsData.map((form) => (
          <Card
            key={form.id}
            className={`transition-all ${
              form?.user_remaining
                ? "opacity-90"
                : "hover:shadow-lg cursor-pointer border-2 border-transparent hover:border-primary/20"
            } flex flex-col justify-between gap-5`}
          >
            <CardHeader className="pb-4">
              <CardTitle className="text-xl leading-tight mb-6 h-[120px]">
                <div className="flex justify-between items-center mb-2">
                  <p className="text-muted-foreground font-thin text-sm">
                    Scheme
                  </p>
                  <Button
                    type="button"
                    variant="link"
                    onClick={() =>
                      handleDownload(form.how_to_apply, `how_to_apply.pdf`)
                    }
                    className="underline"
                  >
                    How To Apply ?
                  </Button>
                </div>
                <p>{form.title}</p>
              </CardTitle>
              {form?.key != "cfc" && (
                <div className="mb-4 p-3 flex justify-between rounded-lg bg-blue-50 border border-blue-200">
                  <p className="text-sm font-semibold">Total Looms Allocated</p>
                  <p className="font-bold text-xl">{form?.total}</p>
                </div>
              )}
              {form?.key != "cfc" && (
                <div className="mb-4 p-3 flex justify-between rounded-lg bg-blue-50 border border-blue-200">
                  <p className="text-sm font-semibold">
                    Number of Looms <br /> Applied for Subsidy
                  </p>
                  <p className="font-bold text-xl">{form?.applied}</p>
                </div>
              )}
              {form?.key != "cfc" && (
                <div className="mb-4 p-3 flex justify-between rounded-lg bg-blue-50 border border-blue-200">
                  <p className="text-sm font-semibold">
                    Number of Looms <br /> Available for Subsidy
                  </p>
                  <p className="font-bold text-xl">{form?.remaining}</p>
                </div>
              )}
              {form?.key == "cfc" && (
                <div className="mb-4 p-3 flex justify-between rounded-lg bg-blue-50 border border-blue-200">
                  <p className="text-sm font-semibold">
                    Total Number of CFC Allocated
                  </p>
                  <p className="font-bold text-xl">{form?.total}</p>
                </div>
              )}
              {form?.key == "cfc" && (
                <div className="mb-4 p-3 flex justify-between rounded-lg bg-blue-50 border border-blue-200">
                  <p className="text-sm font-semibold">
                    Number of CFC <br /> Applied for Subsidy
                  </p>
                  <p className="font-bold text-xl">{form?.applied}</p>
                </div>
              )}
              {form?.key == "cfc" && (
                <div className="mb-4 p-3 flex justify-between rounded-lg bg-blue-50 border border-blue-200">
                  <p className="text-sm font-semibold">
                    Number of CFC <br /> Available for Subsidy
                  </p>
                  <p className="font-bold text-xl">{form?.remaining}</p>
                </div>
              )}
            </CardHeader>

            <CardContent className="pt-0">
              <div className="space-y-4">
                <div className="pt-2 border-t">
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFormClick(form);
                    }}
                    className="w-full"
                    size="sm"
                    variant={form?.user_remaining ? "default" : "outline"}
                    disabled={!form?.user_remaining}
                  >
                    New Application
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="rounded bg-white py-3 px-5">
        <div>
          <h3 className="text-xl font-bold my-3">My Applications</h3>
        </div>
        <Table>
          <TableHeader className="bg-teal-700 rounded">
            <TableRow>
              <TableHead className="text-white font-semibold text-md">
                Application Number
              </TableHead>
              <TableHead className="text-white font-semibold text-md">
                Scheme Type
              </TableHead>
              <TableHead className="text-white font-semibold text-md">
                Applicant Name
              </TableHead>
              <TableHead className="text-white font-semibold text-md">
                Submitted On
              </TableHead>
              <TableHead className="text-white font-semibold text-md">
                Status
              </TableHead>
              <TableHead className="text-white font-semibold text-md">
                Application
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {allApplications.length > 0 ? (
              allApplications.map((application, index) => (
                <TableRow
                  key={`${application.type}-${application.id || index}`}
                >
                  <TableCell className="font-medium">
                    {application.application_no ||
                      application.application_number ||
                      ""}
                  </TableCell>
                  <TableCell>{application.title}</TableCell>
                  <TableCell>
                    {application?.applicant_name ||
                      application?.user?.name ||
                      ""}
                  </TableCell>
                  <TableCell>
                    {application.submitted_on ? (
                      <>
                        {new Date(application.submitted_on)
                          .toLocaleDateString("en-GB")
                          .replace(/\//g, " - ")}
                        <br />
                        {new Date(application.submitted_on).toLocaleTimeString(
                          "en-GB",
                          {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          }
                        )}
                      </>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={application.status} />
                  </TableCell>
                  <TableCell>
                    {application.status != "incomplete" &&
                    application.status != "started" ? (
                      <div className="flex gap-3">
                        {application.status ==
                          "provisional_sanction_recommended" && (
                          <Button
                            variant="outline"
                            asChild
                            className="p-2 text-xs w-full"
                          >
                            <Link
                              to={`/scheme/${application?.type}/${application?.id}`}
                            >
                              Accept Provisional
                              <br /> Sanction Letter
                            </Link>
                          </Button>
                        )}
                        <Button
                          type="button"
                          onClick={() =>
                            handleDownload(
                              application?.application_url,
                              `${application?.application_no}.pdf`
                            )
                          }
                          className="p-2 text-xs w-full"
                        >
                          Download <br /> Application
                        </Button>
                        <Button
                          type="button"
                          onClick={() =>
                            handleDownload(
                              application?.application_uploads,
                              `${application?.application_no}.zip`
                            )
                          }
                          className="p-2 text-xs w-full"
                        >
                          Download <br /> Attachments
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        asChild
                        className="p-2 text-xs w-full"
                      >
                        <Link
                          to={`/scheme/${application?.type}/${application?.id}`}
                        >
                          Continue <br /> Application
                        </Link>
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="text-center text-muted-foreground py-8"
                >
                  No Data Found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default Forms;
